import {
  Button,
  PageRequest,
  PaginationData,
  SearchPagination,
  Sort,
  Table
} from '@elotech/components';
import { AxiosPromise } from 'axios';
import { Arquivo, ArquivoAgrupado, GrupoArquivo } from 'common/type';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';

import { DocumentViewerModal } from '../document-viewer';
import { Informativo } from '../informativo';
import { TableContainer } from '../table';

type Props = {
  grupos: GrupoArquivo[];
  loading?: boolean;
  onDownload: (arquivo: Arquivo) => AxiosPromise;
  ordenarGrupos?: (primeiro: GrupoArquivo, segundo: GrupoArquivo) => number;
  pagination?: PaginationData;
  onPaginar?: (page: PageRequest) => Promise<void>;
  onOrdenar?: (sort: Sort) => void;
  onCarregarVisualizacao?: () => Promise<GrupoArquivo[]>;
  preservarOrdemListagem?: boolean;
  canDownload?: boolean;
};

type ArquivoTabela = ArquivoAgrupado & {
  grupo: string;
};

const ordenarGruposPorOrdem = (primeiro: GrupoArquivo, segundo: GrupoArquivo) =>
  primeiro.ordem - segundo.ordem;

const ArquivoGroup = ({
  grupos,
  loading = false,
  onDownload,
  ordenarGrupos = ordenarGruposPorOrdem,
  pagination,
  onPaginar,
  onOrdenar,
  onCarregarVisualizacao,
  preservarOrdemListagem = false,
  canDownload = true
}: Props) => {
  const [visualizandoArquivos, setVisualizandoArquivos] = useState(false);
  const [gruposVisualizacao, setGruposVisualizacao] =
    useState<GrupoArquivo[]>();
  const [loadingVisualizacao, setLoadingVisualizacao] = useState(false);
  const botaoVisualizarRef = useRef<HTMLButtonElement | null>(null);

  const arquivos = useMemo<ArquivoTabela[]>(
    () =>
      (preservarOrdemListagem
        ? grupos
        : [...grupos].sort(ordenarGrupos)
      ).flatMap(grupo =>
        grupo.arquivos.map(arquivo => ({
          ...arquivo,
          grupo: grupo.descricao
        }))
      ),
    [grupos, ordenarGrupos, preservarOrdemListagem]
  );

  const arquivosVisualizacao = useMemo<ArquivoTabela[]>(
    () =>
      [...(gruposVisualizacao ?? grupos)].sort(ordenarGrupos).flatMap(grupo =>
        grupo.arquivos.map(arquivo => ({
          ...arquivo,
          grupo: grupo.descricao
        }))
      ),
    [grupos, gruposVisualizacao, ordenarGrupos]
  );

  const grupoPorArquivo = useMemo(
    () =>
      new Map(
        arquivosVisualizacao.map(arquivo => [arquivo.arquivo, arquivo.grupo])
      ),
    [arquivosVisualizacao]
  );

  const getDocumentCaption = useCallback(
    (arquivo: Arquivo) => grupoPorArquivo.get(arquivo),
    [grupoPorArquivo]
  );

  const abrirVisualizacao = (event: MouseEvent<HTMLButtonElement>) => {
    botaoVisualizarRef.current = event.currentTarget;
    setVisualizandoArquivos(true);
    if (!onCarregarVisualizacao) {
      setGruposVisualizacao(grupos);
      return;
    }

    setGruposVisualizacao(undefined);
    setLoadingVisualizacao(true);
    onCarregarVisualizacao()
      .then(setGruposVisualizacao)
      .catch(() => setVisualizandoArquivos(false))
      .finally(() => setLoadingVisualizacao(false));
  };

  if (!loading && arquivos.length === 0) {
    return <Informativo type="INFO">Nenhum arquivo encontrado.</Informativo>;
  }

  return (
    <>
      {arquivos.length > 0 && (
        <Button
          type="button"
          color="neutral"
          className="mb-xs"
          onClick={abrirVisualizacao}
        >
          <i className="fa fa-eye" /> Visualizar documentos
        </Button>
      )}
      <TableContainer>
        <Table<ArquivoTabela>
          values={arquivos}
          keyExtractor={arquivo => `${arquivo.origem}-${arquivo.id}`}
          loading={loading}
          sortOnHeaderColumnClick={onOrdenar}
        >
          <Table.Column<ArquivoTabela>
            header="Nome do arquivo"
            name="nome"
            sortable={!!onOrdenar}
            value={arquivo => arquivo.arquivo.nome}
          />
          <Table.Column<ArquivoTabela>
            header="Grupo"
            name="grupo"
            sortable={!!onOrdenar}
            value={arquivo => arquivo.grupo}
          />
          <Table.Column<ArquivoTabela>
            header="Sigiloso"
            name="sigiloso"
            sortable={!!onOrdenar}
            value={arquivo => (arquivo.sigiloso ? 'Sim' : 'Não')}
          />
        </Table>
        {pagination && onPaginar ? (
          <SearchPagination page={pagination} searchWithPage={onPaginar} />
        ) : null}
      </TableContainer>
      {visualizandoArquivos && (
        <DocumentViewerModal
          onClose={() => setVisualizandoArquivos(false)}
          returnFocusRef={botaoVisualizarRef}
          documents={arquivosVisualizacao.map(arquivo => arquivo.arquivo)}
          onDownload={onDownload}
          parentIsLoading={loadingVisualizacao}
          getDocumentCaption={getDocumentCaption}
          canDownload={canDownload}
        />
      )}
    </>
  );
};

export default ArquivoGroup;
export { ArquivoGroup };
